import { z } from "zod";

/**
 * The SPA is built once and promoted unchanged (architecture §12); everything that differs per
 * environment comes from `/config.json` at start-up and is validated here. The Firebase web
 * config is public by design, so `config.json` never holds a secret.
 */
const HostPort = z.string().regex(/^[\w.-]+:\d+$/, "expected host:port");

export const RuntimeConfig = z
  .object({
    env: z.enum(["local", "test", "preview", "staging", "prod"]),
    apiUrl: z.string().url(),
    firebase: z.object({
      apiKey: z.string().min(1),
      authDomain: z.string().min(1),
      projectId: z.string().min(1),
      /**
       * The Firestore database the listeners read: `(default)`, or `pr-<n>` in a preview
       * (architecture §13). Required: nothing assumes the default database.
       */
      firestoreDatabaseId: z.string().regex(/^(\(default\)|[a-z][a-z0-9-]{2,61}[a-z0-9])$/, "expected (default) or a database id"),
      /** Needed for push (FCM registration); null where push is not set up. */
      messagingSenderId: z.string().min(1).nullable().default(null),
      appId: z.string().min(1).nullable().default(null),
    }),
    /**
     * Public key of the project's Web Push certificate (Firebase console → Cloud Messaging, an
     * owner step). Null: push notifications are not configured in this environment.
     */
    messagingVapidKey: z.string().min(1).nullable().default(null),
    useEmulators: z.boolean(),
    emulators: z.object({ auth: HostPort, firestore: HostPort }).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.useEmulators && value.emulators === undefined) {
      ctx.addIssue({ code: "custom", path: ["emulators"], message: "required when useEmulators is true" });
    }
    if (value.useEmulators && (value.env === "staging" || value.env === "prod")) {
      ctx.addIssue({ code: "custom", path: ["useEmulators"], message: `not allowed in ${value.env}` });
    }
  });

export type RuntimeConfig = z.infer<typeof RuntimeConfig>;

export class ConfigError extends Error {
  override name = "ConfigError";
}

/** Parses an already-fetched config value; names every bad field in one error. */
export function parseRuntimeConfig(value: unknown): RuntimeConfig {
  const parsed = RuntimeConfig.safeParse(value);
  if (!parsed.success) {
    const fields = parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ");
    throw new ConfigError(`config.json is invalid: ${fields}`);
  }
  return parsed.data;
}

/** Fetches and validates `/config.json`; the response is never cached by the browser. */
export async function loadRuntimeConfig(fetchFn: typeof fetch, url = "/config.json"): Promise<RuntimeConfig> {
  const res = await fetchFn(url, { cache: "no-store" });
  if (!res.ok) throw new ConfigError(`config.json could not be loaded: HTTP ${res.status}`);
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new ConfigError("config.json is not valid JSON");
  }
  return parseRuntimeConfig(body);
}

/** What web push needs from the config, or null when this environment has not set it up. */
export function pushConfig(config: RuntimeConfig): { vapidKey: string } | null {
  const { messagingSenderId, appId } = config.firebase;
  if (config.messagingVapidKey === null || messagingSenderId === null || appId === null) return null;
  return { vapidKey: config.messagingVapidKey };
}
